import { AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DataLocationDTO, LocationDTO } from '../../../sections/locations-section/models/LocationDTO';
import { DataNamesDTO } from '../../../sections/locations-section/models/DataNamesDTO';
import { Subscription } from 'rxjs';
import { LocationsService } from '../../../sections/locations-section/services/Locations.service';
import { LocationCategoriesService } from '../../../sections/locations-section/services/LocationCategories.service';
import { NotificationsToastService } from 'src/app/features/shared/services/Notifications-Toast.service';

@Component({
  selector: 'app-popup-update-location',
  templateUrl: './popup-update-location.component.html',
  styleUrls: ['./popup-update-location.component.css']
})
export class PopupUpdateLocationComponent implements OnInit, AfterViewInit {

  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLElement>;

  locationDTO: LocationDTO = {} as LocationDTO;

  dataFields: DataLocationDTO[] = [];

  selectedTabIndex: number = 0;

  keyNames: DataNamesDTO[] = [];
  statusOptions: { label: string; value: number }[] = [];

  originalDataLength: number = 0;

  detailsForm: FormGroup = this.formBuilder.group({
    status: [null],
    dataValues: this.formBuilder.array([])
  });

  isLoading: boolean = true;
  fullScreen: boolean = false;
  mobileQuery: MediaQueryList = { matches: false } as MediaQueryList;

  subscription_GetStatuses: Subscription;
  subscription_UpdateLocation: Subscription;

  constructor(
    public dialogRef: MatDialogRef<PopupUpdateLocationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private locationCategoriesService: LocationCategoriesService,
    private locationsService: LocationsService,
    private notificationsToastService: NotificationsToastService
  ) {

  }

  ngOnInit() {
    this.locationDTO = this.data?.locationDTO ?? this.data ?? this.locationDTO;
    this.keyNames = this.data?.keyNames ?? [];
    this.mobileQuery = this.data?.mobileQuery ?? this.mobileQuery;

    const keys: string[] = this.data?.keys ?? [];
    const values: string[] = this.data?.values ?? [];
    this.dataFields = keys.map((key, index) => ({
      idOrder: index,
      key: key,
      value: values[index] ?? '',
      dataType: 'string',
      createdIn: '',
      createdBy: ''
    }));

    this.detailsForm = this.formBuilder.group({
      status: [this.locationDTO.idLocationCategoryStatus],
      dataValues: this.formBuilder.array(this.dataFields.map((item) => this.buildDataGroup(item))),
    });
    this.originalDataLength = this.dataFields.length;

    this.getLocationStatuses();
    this.isLoading = false;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.scrollContainer) {
        this.scrollContainer.nativeElement.scrollTop = 0;
      }
    });
  }

  get dataValuesArray(): FormArray {
    return this.detailsForm.get('dataValues') as FormArray;
  }

  buildDataGroup(dataField: DataLocationDTO): FormGroup {
    return this.formBuilder.group({
      key: [dataField.key],
      value: [dataField.value],
      dataType: [dataField.dataType],
      idOrder: [dataField.idOrder],
    });
  }

  isNewDataField(index: number): boolean {
    return index >= this.originalDataLength;
  }

  addNewDataField() {
    const newDataField: DataLocationDTO = { idOrder: this.dataFields.length, key: '', value: '', dataType: 'string', createdIn: '', createdBy: '' };
    this.dataFields.push(newDataField);
    this.dataValuesArray.push(this.buildDataGroup(newDataField));
  }

  removeDataField(index: number) {
    this.dataFields.splice(index, 1);
    this.dataValuesArray.removeAt(index);
  }

  isDuplicateKey(index: number): boolean {
    const key = (this.dataValuesArray.at(index).get('key').value || '').trim().toLowerCase();
    if (!key) 
      return false;
    
    return this.dataValuesArray.controls.some((control, i) =>
      i !== index && (control.get('key').value || '').trim().toLowerCase() === key
    );
  }

  hasDuplicateKeys(): boolean {
    return this.dataValuesArray.controls.some((data, i) => this.isDuplicateKey(i));
  }

  isEmptyKey(index: number): boolean {
    return this.isNewDataField(index) && !(this.dataValuesArray.at(index).get('key').value || '').trim();
  }

  hasEmptyNewKeys(): boolean {
    return this.dataValuesArray.controls.some((data, i) => this.isEmptyKey(i));
  }

  ngOnDestroy(): void {
    if (this.subscription_GetStatuses) {
      this.subscription_GetStatuses.unsubscribe();
    }
    if (this.subscription_UpdateLocation) {
      this.subscription_UpdateLocation.unsubscribe();
    }
  }

  getLocationStatuses(){
    this.subscription_GetStatuses = this.locationCategoriesService.getCategoryStatusList(this.locationDTO.idLocationCategory).subscribe({
      next: (data) => {
        if(data && data.isSuccess){
          const statuses = data.result;
          this.statusOptions = statuses.map((status) => ({
            label: status.locationStatusValue,
            value: status.id
          }));
          this.detailsForm.get('status')?.setValue(this.locationDTO.idLocationCategoryStatus);
          if (this.statusOptions.length > 0) {
            this.detailsForm.get('status')?.setValidators([Validators.required]);
          } else {
            this.detailsForm.get('status')?.clearValidators();
          }
          this.detailsForm.get('status')?.updateValueAndValidity();
          console.log('Estatus de categoría obtenidos:', this.statusOptions, this.locationDTO.idLocationCategoryStatus) ;
        }
      },
      error: (error) => {
        console.error('Error al obtener los estatus de categoría', error);
      }
    });
  }

  getUsableName(keyName: string): string {
    const keyNameObject = this.keyNames.find((item) => item.keyName == keyName);
    return keyNameObject ? keyNameObject.usableName : keyName;
  }

  //Obtiene la visibilidad según el nombre de la key
  getNameVisibility(keyName) {
    const keyNameObject = this.keyNames.find(
      (item) => item.keyName === keyName
    );
    return keyNameObject ? keyNameObject.isVisible : true;
  }

  private getSelectedStatusId(): number {
    const value = this.detailsForm.get('status')?.value;
    return value === null || value === undefined || value === '' ? null : Number(value);
  }

  CancelButton(){
    this.dialogRef.close();
  }

  SaveButton() {
    if (this.isLoading)
      return;
    this.detailsForm.markAllAsTouched();
    if (this.hasDuplicateKeys() || this.hasEmptyNewKeys())
      return;
    if (this.detailsForm.get('status')?.invalid)
      return;

    this.isLoading = true;
    const dataValues: DataLocationDTO[] = this.dataValuesArray.value;
    const requestLocationDTO: LocationDTO = {
      ...this.locationDTO,
      data: dataValues as any,
      status: 1,
      guid: null,
      idLocationCategoryStatus: this.getSelectedStatusId()
    };
    this.subscription_UpdateLocation = this.locationsService.createOrUpdateLocation(requestLocationDTO).subscribe({
      next: (data) => {
        if(data && data.isSuccess){
          this.dialogRef.close({
            updatedLocation: {
              ...requestLocationDTO
            },
            isUpdated: true
          });
        } else {
          this.notificationsToastService.showError('No se pudo actualizar la ubicación', 'Error al actualizar');
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error al actualizar la ubicación', error);
        this.notificationsToastService.showError('No se pudo actualizar la ubicación', 'Error al actualizar');
        this.isLoading = false;
      }
    });
  }

}
